// src/components/layouts/app/sub/MobileDrawer.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ChevronRight,
  Home,
  Smartphone,
  Shirt,
  Heart,
  Sparkles,
  Dumbbell,
  Utensils,
  Baby,
  Tag,
  Zap,
  TrendingUp,
  Clock,
  Truck,
  Shield,
  Headphones,
  User,
  ShoppingBag,
  HelpCircle,
  Info,
  Mail,
  FileText,
  LogOut,
  Settings,
  Award,
} from "lucide-react";
import { CategoryItem } from "../types";

interface MobileDrawerProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
  categories: CategoryItem[];
  getCategorySlug?: (cat: string) => string;
  userName?: string;
  cartItemCount?: number;
}

const mobileIconMap: { [key: string]: React.ReactNode } = {
  "maison-jardin": <Home className="w-5 h-5" />,
  electronique: <Smartphone className="w-5 h-5" />,
  "mode-homme": <Shirt className="w-5 h-5" />,
  "mode-femme": <Heart className="w-5 h-5" />,
  "beaute-sante": <Sparkles className="w-5 h-5" />,
  "sport-loisirs": <Dumbbell className="w-5 h-5" />,
  alimentation: <Utensils className="w-5 h-5" />,
  "enfants-bebe": <Baby className="w-5 h-5" />,
  default: <Home className="w-5 h-5" />,
};

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isMenuOpen,
  setIsMenuOpen,
  categories,
  getCategorySlug = (cat: string) =>
    cat.toLowerCase().replace(/ & /g, "-").replace(/ /g, "-"),
  userName = "Mon Compte",
  cartItemCount = 0,
}) => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const featuredLinks = [
    { id: "new", name: "Nouveautés", icon: <Zap className="w-4 h-4" />, color: "text-blue-600", bg: "bg-blue-50" },
    { id: "trending", name: "Tendances", icon: <TrendingUp className="w-4 h-4" />, color: "text-orange-600", bg: "bg-orange-50" },
    { id: "deals", name: "Promotions", icon: <Tag className="w-4 h-4" />, color: "text-green-600", bg: "bg-green-50" },
    { id: "fast", name: "Livraison Rapide", icon: <Clock className="w-4 h-4" />, color: "text-purple-600", bg: "bg-purple-50" },
  ];

  const serviceLinks = [
    { id: "delivery", name: "Livraison", icon: <Truck className="w-4 h-4" /> },
    { id: "security", name: "Sécurité", icon: <Shield className="w-4 h-4" /> },
    { id: "help", name: "Centre d'aide", icon: <Headphones className="w-4 h-4" /> },
  ];

  const infoLinks = [
    { id: "seller", name: "Devenir vendeur", icon: <User className="w-4 h-4" /> },
    { id: "about", name: "À propos", icon: <Info className="w-4 h-4" /> },
    { id: "contact", name: "Contact", icon: <Mail className="w-4 h-4" /> },
    { id: "terms", name: "Conditions", icon: <FileText className="w-4 h-4" /> },
  ];

  const accountLinks = [
    { id: "profile", name: "Mon profil", icon: <User className="w-4 h-4" />, path: "/account" },
    { id: "orders", name: "Mes commandes", icon: <ShoppingBag className="w-4 h-4" />, path: "/orders" },
    { id: "settings", name: "Paramètres", icon: <Settings className="w-4 h-4" />, path: "/settings" },
    { id: "logout", name: "Déconnexion", icon: <LogOut className="w-4 h-4" />, path: "/logout" },
  ];

  return (
    <AnimatePresence>
      {isMenuOpen && (
        <>
          {/* Overlay plein écran avec flou */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-md z-50 lg:hidden"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Drawer plein écran */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 180 }}
            className="fixed top-0 left-0 w-full h-full bg-white z-50 overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header avec dégradé et ombre */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-purple-700 via-purple-600 to-indigo-600 shadow-lg">
              <div className="flex items-center justify-between px-5 py-4">
                <Link to="/" className="flex items-center gap-3">
                  <div className="relative rounded-md">
                    <img
                      src="/assets/logo/notextfavicon.jpeg"
                      alt="Kayovex"
                      className="w-9 h-9 object-contain rounded-lg "
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold bg-gradient-to-r from-white to-white bg-clip-text text-transparent">
                      Kayovex
                    </span>
                    <span className="text-xs text-white ">
                      Marketplace
                    </span>
                  </div>
                </Link>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 rounded-full hover:bg-white/20 transition-all duration-200 active:scale-95"
                  aria-label="Fermer"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            {/* Section utilisateur */}
            <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl shadow-md">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{userName}</h3>
                    <p className="text-xs text-gray-500">Retrouvez vos avantages</p>
                  </div>
                </div>
                <Link
                  to="/cart"
                  className="relative p-2.5 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <ShoppingBag className="w-5 h-5 text-gray-700" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold min-w-[20px] h-[20px] flex items-center justify-center rounded-full shadow-md">
                      {cartItemCount > 9 ? "9+" : cartItemCount}
                    </span>
                  )}
                </Link>
              </div>
            </div>

            {/* Contenu scrollable */}
            <div className="px-5 pb-24 pt-3">
              {/* Actions rapides */}
              <div className="mb-8">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Quick Access
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    to="/deals"
                    className="group p-4 rounded-xl bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 flex flex-col items-center justify-center hover:shadow-md transition-all active:scale-95"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="p-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg mb-2 shadow-md group-hover:scale-105 transition">
                      <Tag className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-orange-800">Promos</span>
                    <span className="text-xs text-orange-600 mt-0.5">-50% Max</span>
                  </Link>
                  <Link
                    to="/new"
                    className="group p-4 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 flex flex-col items-center justify-center hover:shadow-md transition-all active:scale-95"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg mb-2 shadow-md group-hover:scale-105 transition">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-blue-800">Nouveautés</span>
                    <span className="text-xs text-blue-600 mt-0.5">Voir les arrivages</span>
                  </Link>
                </div>
              </div>

              {/* Catégories */}
              <div className="mb-8">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Catégories
                </h3>
                <div className="space-y-1 bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                  {categories.map((category) => (
                    <div key={category.id} className="border-b border-gray-50 last:border-0">
                      <button
                        onClick={() => toggleCategory(category.id)}
                        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`p-2 rounded-lg transition-all ${expandedCategory === category.id
                                ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-md"
                                : "bg-gray-100 text-gray-600"
                              }`}
                          >
                            {mobileIconMap[category.slug] || mobileIconMap.default}
                          </div>
                          <div className="text-left">
                            <span className="font-medium text-gray-800 block">
                              {category.name}
                            </span>
                            {category.description && (
                              <span className="text-xs text-gray-500">
                                {category.description}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {category.badge && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r from-red-100 to-orange-100 text-red-700 font-bold">
                              {category.badge}
                            </span>
                          )}
                          <ChevronRight
                            className={`w-4 h-4 transition-transform duration-300 ${expandedCategory === category.id
                                ? "rotate-90 text-purple-600"
                                : "text-gray-400"
                              }`}
                          />
                        </div>
                      </button>

                      <AnimatePresence>
                        {expandedCategory === category.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <div className="pl-14 pr-3 pb-3">
                              <div className="grid grid-cols-2 gap-2">
                                {category.subcategories?.map((sub) => (
                                  <Link
                                    key={sub.id}
                                    to={`/category/${category.slug}/${sub.slug}`}
                                    className="p-2.5 text-sm text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors border border-gray-200 text-center active:scale-95"
                                    onClick={() => setIsMenuOpen(false)}
                                  >
                                    {sub.name}
                                  </Link>
                                ))}
                              </div>
                              <Link
                                to={`/category/${category.slug}`}
                                className="mt-3 text-sm font-semibold text-purple-600 flex items-center justify-center gap-1 hover:text-purple-700"
                                onClick={() => setIsMenuOpen(false)}
                              >
                                Tout voir <ChevronRight className="w-3.5 h-3.5" />
                              </Link>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>

              {/* En vedette */}
              <div className="mb-8">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  En vedette
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {featuredLinks.map((item) => (
                    <Link
                      key={item.id}
                      to={`/${item.id}`}
                      className="p-3 rounded-xl border border-gray-200 hover:border-gray-300 transition-all flex items-center gap-3 hover:shadow-sm active:scale-95 bg-white"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <div className={`p-2 rounded-lg ${item.bg} ${item.color}`}>{item.icon}</div>
                      <span className="text-sm font-medium text-gray-700">{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Services */}
              <div className="mb-8">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Services
                </h3>
                <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                  {serviceLinks.map((service, idx) => (
                    <React.Fragment key={service.id}>
                      {idx > 0 && <div className="h-px bg-gray-100 mx-3" />}
                      <Link
                        to={`/${service.id}`}
                        className="flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <div className="p-2 bg-gray-100 rounded-lg text-gray-600">{service.icon}</div>
                        <span className="text-sm text-gray-700">{service.name}</span>
                        <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                      </Link>
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Compte & Infos en deux colonnes */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                    Mon compte
                  </h3>
                  <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                    {accountLinks.map((link, idx) => (
                      <React.Fragment key={link.id}>
                        {idx > 0 && <div className="h-px bg-gray-100 mx-3" />}
                        <Link
                          to={link.path}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="p-1.5 text-gray-500">{link.icon}</div>
                          <span className="text-sm text-gray-700">{link.name}</span>
                        </Link>
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                    Informations
                  </h3>
                  <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                    {infoLinks.map((info, idx) => (
                      <React.Fragment key={info.id}>
                        {idx > 0 && <div className="h-px bg-gray-100 mx-3" />}
                        <Link
                          to={`/${info.id}`}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="p-1.5 text-gray-500">{info.icon}</div>
                          <span className="text-sm text-gray-700">{info.name}</span>
                        </Link>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>

              {/* Assistance */}
              <div className="pt-4 border-t border-gray-200">
                <Link
                  to="/help"
                  className="flex items-center justify-center gap-2 p-3.5 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 hover:shadow-md transition-all active:scale-95"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <HelpCircle className="w-5 h-5 text-purple-600" />
                  <span className="font-medium text-purple-700">Assistance 24/7</span>
                </Link>
              </div>

              {/* Badge de version */}
              <div className="mt-6 text-center text-[10px] text-gray-400">Version 2.0.0</div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileDrawer;